import React, { useEffect, useRef } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { podeAbrirRota } from '@/app/registry/routes';
import TabErrorBoundary from './TabErrorBoundary';
import { buildTabKey } from './TabRouteSync';
import { useTabs } from './TabsProvider';

const TabHost: React.FC = () => {
  const location = useLocation();
  const outlet = useOutlet();
  const currentKey = buildTabKey(location.pathname, location.search);
  const { activeKey, tabs } = useTabs();
  const { hasPermission } = useAuth();
  const cacheRef = useRef(new Map<string, React.ReactNode>());

  useEffect(() => {
    if (outlet) {
      cacheRef.current.set(currentKey, outlet);
    }
  }, [currentKey, outlet]);

  useEffect(() => {
    const allowedKeys = new Set(tabs.map((tab) => tab.key));
    cacheRef.current.forEach((_, key) => {
      if (!allowedKeys.has(key) && key !== currentKey) {
        cacheRef.current.delete(key);
      }
    });
  }, [currentKey, tabs]);

  // Rota sem permissão não ganha aba; renderiza direto para o ProtectedRoute dela redirecionar (28/10/2026).
  if (tabs.length === 0 || !podeAbrirRota(location.pathname, hasPermission)) {
    return <TabErrorBoundary key={currentKey}>{outlet}</TabErrorBoundary>;
  }

  return (
    <>
      {tabs.map((tab) => {
        const cachedOutlet = cacheRef.current.get(tab.key);
        if (!cachedOutlet) return null;
        const isActive = tab.key === activeKey;

        return (
          <section
            key={tab.key}
            aria-hidden={!isActive}
            className={cn('min-h-full w-full', isActive ? 'block animate-in fade-in duration-150' : 'hidden')}
          >
            <TabErrorBoundary>{cachedOutlet}</TabErrorBoundary>
          </section>
        );
      })}
    </>
  );
};

export default TabHost;
